import { PayloadAction } from '@reduxjs/toolkit';

import { DesignState, MediaText } from 'editor/src/store/design/types';

interface ElementYUpdate {
  uuid: number;
  y?: number;
  x?: number;
}

interface RowShiftUpdate {
  /** See ReflowRowShiftUpdate in computeReflowState.ts for why groupId scopes
   *  the update — without it, two groups carrying rows with identical ids
   *  would collide. */
  groupId: string;
  rowId: string;
  shiftY?: number;
  elementShiftsX?: Record<string, number>;
}

/**
 * Applies the position part of the reflow state (the hide part flows through
 * `updateHiddenElementsAction`). Mutates `element.y` for the given uuids and
 * `row.shiftY` for the given row ids on the target spread.
 *
 * Do NOT dispatch this directly — go through `updateHiddenElementsOperation`
 * so the hide channel and the position channel stay in sync.
 */
const applyReflowStateReducer = (
  state: DesignState,
  action: PayloadAction<{
    spreadIndex: number;
    elementUpdates: ElementYUpdate[];
    rowShiftUpdates: RowShiftUpdate[];
  }>,
) => {
  if (!state.designData) {
    return;
  }
  const { spreadIndex, elementUpdates, rowShiftUpdates } = action.payload;
  const spread = state.designData.spreads[spreadIndex];
  if (!spread) {
    return;
  }

  if (elementUpdates.length) {
    const yByUuid = new Map<number, number>();
    const xByUuid = new Map<number, number>();
    elementUpdates.forEach((u) => {
      if (u.y !== undefined) {
        yByUuid.set(u.uuid, u.y);
      }
      if (u.x !== undefined) {
        xByUuid.set(u.uuid, u.x);
      }
    });
    spread.pages.forEach((page) => {
      page.groups.media?.forEach((element) => {
        if (element.type !== 'text') {
          return;
        }
        const newY = yByUuid.get(element.uuid);
        if (newY !== undefined) {
          (element as MediaText).y = newY;
        }
        const newX = xByUuid.get(element.uuid);
        if (newX !== undefined) {
          (element as MediaText).x = newX;
        }
      });
    });
  }

  if (rowShiftUpdates.length && spread.reflowGroups) {
    // Key by `${groupId}::${rowId}` so two groups holding rows with the same
    // rowId do not collide. The "::" separator is fine because row ids are
    // generated alphanumerically (no "::" in them).
    const keyOf = (groupId: string, rowId: string): string => `${groupId}::${rowId}`;
    const updatesByCompositeKey = new Map<string, RowShiftUpdate>();
    rowShiftUpdates.forEach((u) => updatesByCompositeKey.set(keyOf(u.groupId, u.rowId), u));
    spread.reflowGroups.forEach((group) => {
      group.rows.forEach((row) => {
        const update = updatesByCompositeKey.get(keyOf(group.id, row.id));
        if (!update) {
          return;
        }
        if (update.shiftY !== undefined) {
          row.shiftY = update.shiftY === 0 ? undefined : update.shiftY;
        }
        if (update.elementShiftsX !== undefined) {
          const allZero = Object.values(update.elementShiftsX).every((v) => v === 0);
          row.elementShiftsX = allZero ? undefined : update.elementShiftsX;
        }
      });
    });
  }
};

export default applyReflowStateReducer;
